import clsx from 'clsx';
import { NumberField, TextField } from 'components';
import { useCalculator } from 'contexts/Calculator/Calculator';
import { useTooltip } from 'contexts/Tooltip/Tooltip';
import { formatNumber, getUrl } from 'helpers';
import { createRef, useState } from 'react';
import MarginOfErrorInfo from './MarginOfErrorInfo';
import PopulationSizeError from './PopulationSizeError';
import ResultsInfo from './ResultsInfo';
import SampleSizeInfo from './SampleSizeInfo';

export default function Variant({ index, label, name, result, resultType, sampleSize }: Variant) {

    const [copied, setCopied] = useState(false);
    const calculator = useCalculator();
    const tooltip = useTooltip();

    const fieldRefs: any = {
        result: createRef(),
        sampleSize: createRef(),
    };

    const results = calculator.results[index];
    const sampleConverted = result !== undefined && sampleSize !== undefined ? (Math.round(result * sampleSize * 1000) / 100000) : 0;

    function handleChange(value: any, name: string) {
        calculator.updateVariant(index, name, value);
    }

    async function handleCopy() {
        const text = [
            `${label}`,
            `Sample Size: ${formatNumber(sampleSize || 0)}`,
            `Results: ${results?.resultPercentage}% ± ${results?.marginOfErrorPercentage}%`,
            `Range: ${results?.minValue}% - ${results?.maxValue}%`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            // Clipboard access denied (e.g. insecure context, no user gesture, iframe policy)
        }
    }

    function getAllowed(value: any, name: string) {
        if (!calculator.populationSize && value) {
            const field = fieldRefs[name].current;
            const inputRect = field.querySelector('input').getBoundingClientRect();
            const x = inputRect.left + inputRect.width / 2;
            const y = inputRect.top + inputRect.height / 2;
            tooltip.show(x, y,
                <PopulationSizeError />,
            );
        }

        return !!calculator.populationSize;
    }

    function handleEditSubmit(e: any) {
        e.preventDefault();
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    function handleResultTypeChange(type: NumberFieldType) {
        calculator.updateVariant(index, 'resultType', type);
    }

    const cardClasses = clsx('variant', results?.evaluation);

    return <div className={cardClasses} data-testid={`variant-card-${index}`}>
        {results?.evaluation && (
            <div className="variant-header" data-testid={`variant-status-${index}`}>{results.evaluation}</div>
        )}
        {calculator.variants.length > 1 && (
            <button
                aria-label="Remove variant"
                className="close"
                data-testid={`variant-${index}-remove`}
                type="button"
                onClick={() => calculator.removeVariant(index)}
            />
        )}
        <div className="variant-label">
            <form onSubmit={handleEditSubmit}>
                <TextField name="name" placeholder={label} value={name} onChange={handleChange} />
            </form>
            <h3 data-testid={`variant-${index}-heading`}>{label}</h3>
        </div>
        <NumberField
            aria-label="Sample Size"
            className="mb-4"
            data-testid={`variant-${index}-sample-size`}
            fieldRef={fieldRefs.sampleSize}
            highlight
            info={SampleSizeInfo}
            label="Sample Size"
            max={calculator.populationSize}
            name="sampleSize"
            placeholder="ex. 18,000"
            value={sampleSize}
            getAllowed={getAllowed}
            onChange={handleChange}
        />
        {calculator.populationSize !== undefined && sampleSize !== undefined && calculator.populationSize < sampleSize && (
            <p className="note">You have a <i>Population Size</i> of {formatNumber(calculator.populationSize)}, so your sample size must be smaller.</p>
        )}
        <NumberField
            aria-label="Results"
            className="mb-4"
            data-testid={`variant-${index}-results`}
            fieldRef={fieldRefs.result}
            highlight
            info={ResultsInfo}
            label="Results"
            max={resultType === 'percentage' ? 100 : calculator.populationSize}
            name="result"
            placeholder={resultType === 'percentage' ? 'ex. 20.3%' : 'ex. 1324'}
            type={resultType}
            value={result}
            getAllowed={getAllowed}
            onChange={handleChange}
            onTypeChange={handleResultTypeChange}
        />
        {resultType === 'percentage' && result !== undefined && sampleSize !== undefined && (sampleConverted.toString().split('.')[1]?.length || 0) > 2 && (
            <p className="note">Your {formatNumber(result)}% <i>Results</i> show that in your {formatNumber(sampleSize)} <i>Sample Size</i>, {formatNumber(sampleConverted)} from your sample converted. Since this is a fraction, you may have intended to show results as a number and not a percentage.</p>
        )}
        {results && (
            <div className="variant-results">
                <label>
                    Results with Margin of Error
                    <MarginOfErrorInfo />
                </label>
                <p>
                    {results.resultPercentage}% ± {results.marginOfErrorPercentage}%
                </p>
                {isNaN(results.marginOfErrorPercentage) && (
                    <p className="note">
                        Your <i>Results</i> have created a division by zero error and do not appear to be valid. Please double check your data.
                    </p>
                )}
                <button
                    aria-label="Copy results to clipboard"
                    className="variant-results-copy"
                    data-testid={`variant-${index}-copy-results`}
                    type="button"
                    onClick={handleCopy}
                >
                    <picture>
                        <img alt="" aria-hidden src={getUrl('/assets/copy.svg')} />
                    </picture>
                    {copied && (
                        <div className="variant-results-copy-tooltip">
                            Copied
                        </div>
                    )}
                </button>
            </div>
        )}
    </div>;

};
